import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Trash2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BendDeductions } from "@/components/config/BendDeductions";
import { SalePolicyFields } from "@/components/config/SalePolicyFields";
import { MaterialsPriceList } from "@/components/price_lists/MaterialsPriceList";
import { ReaderKnowledge } from "@/components/config/ReaderKnowledge";

const emptyMachine = { name: "", type: "laser", rate_h: 70, cut_speed_mm_min: 4000, cost_per_pierce: 0.08, setup_min: 15, max_thickness_mm: 15 };
const BENDING_FIELDS = [["rate_h", "Tariffa piegatura (€/h)", "0.01"], ["setup_min", "Attrezzaggio (min/lotto)", "0.5"], ["time_per_bend_min", "Tempo per piega (min/operazione)", "0.001"], ["complexity_surcharge_pct", "Maggiorazione complessità (%)", "0.1"], ["min_cost", "Minimo piegatura (€/lotto)", "0.01"]];

export default function Config() {
  const { tenant } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cfg, setCfg] = useState({ materials: [], machines: [], bending: {}, overhead: {} });
  const [mac, setMac] = useState(emptyMachine);
  const [macOpen, setMacOpen] = useState(false);

  const load = async () => { try { const { data } = await api.get("/config"); setCfg(data); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  useEffect(() => { load(); }, []);

  const saveMac = async (e) => { e.preventDefault();
    try { await api.post("/config/machines", mac); toast.success("Macchina creata"); setMacOpen(false); setMac(emptyMachine); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const delMac = async (id) => { await api.delete(`/config/machines/${id}`); toast.success("Eliminata"); load(); };

  const saveBending = async () => { try { const { data } = await api.put("/config/bending", cfg.bending); setCfg(old => ({ ...old, bending: data })); toast.success("Piegatura aggiornata. Revisioni esistenti invariate."); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const saveOverhead = async () => { try { const { data } = await api.put("/config/overhead", cfg.overhead); setCfg(old => ({ ...old, overhead: data })); toast.success("Costi generali aggiornati"); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };

  return (
    <div className="opaque-editable-fields p-4 sm:p-8 min-w-0" data-testid="config-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Officina</div>
        <h1 className="text-3xl font-display font-bold mt-1">Configurazione costi</h1>
      </div>
      <Tabs value={searchParams.get("tab") || "materials"} onValueChange={tab => setSearchParams({ tab })}>
        <TabsList className="flex flex-wrap h-auto w-fit max-w-full gap-1 justify-start">
          <TabsTrigger value="materials" data-testid="tab-materials">Materiali</TabsTrigger>
          <TabsTrigger value="machines" data-testid="tab-machines">Macchine</TabsTrigger>
          <TabsTrigger value="bending" data-testid="tab-bending">Piegatura</TabsTrigger>
          <TabsTrigger value="deductions" data-testid="tab-deductions">Ritiri tabellati</TabsTrigger>
          <TabsTrigger value="overhead" data-testid="tab-overhead">Costi generali</TabsTrigger>
          <TabsTrigger value="knowledge" data-testid="tab-knowledge">Conoscenza lettore</TabsTrigger>
        </TabsList>

        <TabsContent value="knowledge"><ReaderKnowledge key={tenant?.id} /></TabsContent>

        <TabsContent value="deductions"><BendDeductions key={tenant?.id} tenantName={tenant?.name} /></TabsContent>

        <TabsContent value="materials"><div className="min-w-0"><MaterialsPriceList key={tenant?.id} /></div></TabsContent>

        <TabsContent value="machines"><fieldset disabled={!cfg.editable} className="min-w-0">
          <div className="flex justify-end mb-3">
            <Dialog open={macOpen} onOpenChange={setMacOpen}>
              <DialogTrigger asChild><Button data-testid="btn-new-machine">+ Macchina</Button></DialogTrigger>
              <DialogContent className="opaque-editable-fields">
                <DialogHeader><DialogTitle>Nuova macchina</DialogTitle></DialogHeader>
                <form onSubmit={saveMac} className="grid grid-cols-2 gap-3 text-sm">
                  <div className="col-span-2"><Label>Nome</Label><Input value={mac.name} onChange={(e) => setMac({...mac, name: e.target.value})} required data-testid="mac-name" /></div>
                  <div><Label>Tipo</Label><Input value={mac.type} onChange={(e) => setMac({...mac, type: e.target.value})} placeholder="laser, plasma, waterjet, press_brake" /></div>
                  <div><Label>€/h</Label><Input type="number" step="0.01" value={mac.rate_h} onChange={(e) => setMac({...mac, rate_h: parseFloat(e.target.value)})} data-testid="mac-rate" /></div>
                  <div><Label>Velocità taglio mm/min</Label><Input type="number" value={mac.cut_speed_mm_min} onChange={(e) => setMac({...mac, cut_speed_mm_min: parseFloat(e.target.value)})} /></div>
                  <div><Label>Costo sfondamento €</Label><Input type="number" step="0.001" value={mac.cost_per_pierce} onChange={(e) => setMac({...mac, cost_per_pierce: parseFloat(e.target.value)})} /></div>
                  <div><Label>Setup (min)</Label><Input type="number" value={mac.setup_min} onChange={(e) => setMac({...mac, setup_min: parseFloat(e.target.value)})} /></div>
                  <div><Label>Spess. max mm</Label><Input type="number" value={mac.max_thickness_mm} onChange={(e) => setMac({...mac, max_thickness_mm: parseFloat(e.target.value)})} /></div>
                  <Button type="submit" className="col-span-2" data-testid="mac-save">Salva</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="border border-border bg-white">
            <table className="dense-table w-full">
              <thead><tr><th>Nome</th><th>Tipo</th><th>€/h</th><th>V. taglio</th><th>Sfond.</th><th>Setup</th><th></th></tr></thead>
              <tbody>
                {cfg.machines.map(m => (
                  <tr key={m.id}>
                    <td className="font-medium">{m.name}</td><td className="uppercase text-[10px] tracking-widest">{m.type}</td>
                    <td className="font-mono">{m.rate_h?.toFixed(2)}</td><td className="font-mono">{m.cut_speed_mm_min || "—"}</td>
                    <td className="font-mono">{m.cost_per_pierce || "—"}</td><td className="font-mono">{m.setup_min} min</td>
                    <td><button onClick={() => delMac(m.id)} className="text-destructive"><Trash2 size={12} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </fieldset></TabsContent>

        <TabsContent value="bending"><fieldset disabled={!cfg.editable} className="min-w-0">
          <div className="max-w-xl border border-border bg-white p-6 grid grid-cols-2 gap-4">
            {BENDING_FIELDS.map(([k, label, step]) => (
              <div key={k}>
                <Label>{label}</Label>
                <Input type="number" step={step} data-testid={`bending-${k}`} value={cfg.bending?.[k] ?? 0}
                  onChange={(e) => setCfg({...cfg, bending: {...cfg.bending, [k]: parseFloat(e.target.value)}})} />
              </div>
            ))}
            <div className="col-span-2">
              <Label>Ambito del minimo piegatura</Label>
              <select className="border bg-white w-full h-10 min-w-0 px-2 text-sm" aria-label="Ambito del minimo piegatura" data-testid="bending-min_cost_scope" value={cfg.bending?.min_cost_scope || ""}
                onChange={(e) => setCfg({...cfg, bending: {...cfg.bending, min_cost_scope: e.target.value || null}})}>
                <option value="">— Non registrato: da confermare in ogni bozza —</option>
                <option value="OPERATION_LOT_ONLY">Sola esecuzione del lotto (attrezzaggio separato, sempre aggiunto)</option>
                <option value="COMPONENT_LOT_WITH_SETUP">Esecuzione + attrezzaggio del lotto</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1" data-testid="bending-min_cost_scope-help">Il minimo è una soglia, mai un importo fisso aggiuntivo: adeguamento = max(0; minimo − base del lotto). Le revisioni già salvate restano invariate; una scelta fatta nella bozza prevale.</p>
            </div>
            <Button className="col-span-2" onClick={saveBending} data-testid="bending-save">Salva piegatura</Button>
          </div>
        </fieldset></TabsContent>

        <TabsContent value="overhead"><fieldset disabled={!cfg.editable} className="min-w-0">
          <div className="max-w-2xl border border-border bg-white p-6 grid grid-cols-2 gap-4">
            <SalePolicyFields value={cfg.overhead || {}} onChange={overhead => setCfg(old => ({ ...old, overhead }))} />
            {["programming_cost","order_mgmt_cost","packaging","transport","overhead_pct","min_margin_pct","rush_surcharge_pct","tax_pct","quote_validity_days","min_batch"].map(k => (
              <div key={k}>
                <Label className="capitalize">{k.replace(/_/g," ")}</Label>
                <Input type="number" step="0.01" data-testid={`ov-${k}`} value={cfg.overhead?.[k] ?? 0}
                  onChange={(e) => setCfg({...cfg, overhead: {...cfg.overhead, [k]: parseFloat(e.target.value)}})} />
              </div>
            ))}
            <Button className="col-span-2" onClick={saveOverhead} data-testid="overhead-save">Salva costi generali</Button>
          </div>
        </fieldset></TabsContent>
      </Tabs>
    </div>
  );
}
